import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { SUPPORTED_LOCALES, useLocale } from '../contexts/LocaleContext';

const copy = {
  en: {
    create: 'Create Account', subtitle: 'Choose your business edition and language before you begin.',
    company: 'Company Name', companyPlaceholder: 'Your Business Ltd', region: 'Business location and edition',
    regionHelp: 'Sets the starting currency, timezone, tax jurisdiction and document defaults. You can change these later in Settings.',
    language: 'Application and help language', languageHelp: 'This language is used immediately and remains editable in Settings.',
    first: 'First Name', last: 'Last Name', email: 'Email', password: 'Password',
    submit: 'Create Account', submitting: 'Creating account...', required: 'Please fill in all fields',
    success: 'Account created successfully!', failed: 'Registration failed', existing: 'Already have an account?', signIn: 'Sign in',
    agreement: 'By creating an account, you agree to the Terms and acknowledge the Privacy Notice and Data Processing information.',
  },
  de: {
    create: 'Konto erstellen', subtitle: 'Wähle zuerst Unternehmensregion und Sprache aus.',
    company: 'Firmenname', companyPlaceholder: 'Ihr Unternehmen', region: 'Unternehmensstandort und Edition',
    regionHelp: 'Legt Währung, Zeitzone, Steuergebiet und Dokumentvorgaben fest. Alles bleibt später in den Einstellungen änderbar.',
    language: 'Anwendungs- und Hilfesprache', languageHelp: 'Diese Sprache wird sofort verwendet und bleibt in den Einstellungen änderbar.',
    first: 'Vorname', last: 'Nachname', email: 'E-Mail', password: 'Passwort',
    submit: 'Konto erstellen', submitting: 'Konto wird erstellt...', required: 'Bitte fülle alle Felder aus',
    success: 'Konto wurde erfolgreich erstellt!', failed: 'Registrierung fehlgeschlagen', existing: 'Du hast bereits ein Konto?', signIn: 'Anmelden',
    agreement: 'Mit der Kontoerstellung stimmst du den Nutzungsbedingungen zu und nimmst Datenschutzerklärung und Auftragsverarbeitung zur Kenntnis.',
  },
};

const regionLocaleDefaults = { CA: 'en-CA', US: 'en-US', DE_EU: 'de-DE' };

export default function RegisterPage() {
  const { register } = useAuth();
  const { language, locale, setLocale } = useLocale();
  const text = copy[language];
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: '', email: '', password: '', first_name: '', last_name: '', operating_region: 'CA',
    default_locale: locale, default_document_locale: locale,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleRegionChange = (e) => {
    const operating_region = e.target.value;
    const nextLocale = regionLocaleDefaults[operating_region];
    setLocale(nextLocale);
    setForm({ ...form, operating_region, default_locale: nextLocale, default_document_locale: nextLocale });
  };
  const handleLocaleChange = (e) => {
    const nextLocale = e.target.value;
    setLocale(nextLocale);
    setForm({ ...form, default_locale: nextLocale, default_document_locale: nextLocale });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.password || !form.first_name || !form.last_name) {
      toast.error(text.required);
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(text.success);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || text.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">SF</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ServiceFlow</h1>
        </div>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-semibold text-slate-900">{text.create}</CardTitle>
            <CardDescription>{text.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">{text.company}</Label>
                <Input id="company_name" name="company_name" placeholder={text.companyPlaceholder} value={form.company_name} onChange={handleChange} data-testid="register-company" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="operating_region">{text.region}</Label>
                <select
                  id="operating_region"
                  name="operating_region"
                  value={form.operating_region}
                  onChange={handleRegionChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="register-operating-region"
                >
                  <option value="CA">Canada · English · CAD</option>
                  <option value="US">United States · English · USD</option>
                  <option value="DE_EU">Germany / Europe · Deutsch · EUR</option>
                </select>
                <p className="text-xs text-slate-500">{text.regionHelp}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_locale">{text.language}</Label>
                <select
                  id="default_locale"
                  value={form.default_locale}
                  onChange={handleLocaleChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="register-language"
                >
                  {SUPPORTED_LOCALES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
                <p className="text-xs text-slate-500">{text.languageHelp}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{text.first}</Label>
                  <Input id="first_name" name="first_name" placeholder="John" value={form.first_name} onChange={handleChange} data-testid="register-firstname" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{text.last}</Label>
                  <Input id="last_name" name="last_name" placeholder="Doe" value={form.last_name} onChange={handleChange} data-testid="register-lastname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{text.email}</Label>
                <Input id="email" name="email" type="email" placeholder="john@company.ca" value={form.email} onChange={handleChange} data-testid="register-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{text.password}</Label>
                <Input id="password" name="password" type="password" placeholder="Create a strong password" value={form.password} onChange={handleChange} data-testid="register-password" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="register-submit">
                {loading ? text.submitting : text.submit}
              </Button>
              <p className="text-xs leading-5 text-slate-500">{text.agreement} <Link to="/legal" target="_blank" className="text-blue-600 hover:underline">Legal / Compliance</Link></p>
            </form>
            <p className="text-center text-sm text-slate-500 mt-4">
              {text.existing}{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-medium">{text.signIn}</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
